import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiFetch } from "../api/client";
import { ErrorBanner } from "../components/ErrorBanner";
import { QuestionImages } from "../components/QuestionImages";
import type { QuestionImageRef } from "../api/types";

// Local types: deliberately narrower than the admin ones — `is_correct` is
// not on the wire to students and is not modeled here either, so the field
// cannot accidentally be read or rendered.
interface StudentAnswerOption {
  id: string;
  text: string;
  position: number;
}
interface StudentQuestion {
  id: string;
  type: "mcq" | "free_text";
  prompt: string;
  points: number;
  position: number;
  answer_options: StudentAnswerOption[];
  images?: QuestionImageRef[];
}
interface StudentTest {
  id: string;
  title: string;
  description: string | null;
  status: "draft" | "active" | "closed" | "archived";
  starts_at: string | null;
  ends_at: string | null;
  duration_minutes: number | null;
  questions: StudentQuestion[];
}
interface StudentSubmissionEcho {
  id: string;
  status: "draft" | "submitted" | "graded";
  started_at?: string | null;
  effective_deadline?: string | null;
  answers: Array<{
    question_id: string;
    text_answer: string | null;
    selected_option_ids: string[];
  }>;
}

interface AnswerLocal {
  selected_option_ids: string[];
  text_answer: string;
}

interface SubmitResponse {
  submission: {
    id: string;
    status: "submitted" | "graded";
    submitted_at: string | null;
    auto_submitted_at: string | null;
    total_auto_score: number | null;
  };
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export function StudentTestPage() {
  const params = useParams<{ testId: string }>();
  const testId = params.testId!;

  const [test, setTest] = useState<StudentTest | null>(null);
  const [submission, setSubmission] = useState<StudentSubmissionEcho | null>(
    null,
  );
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [now, setNow] = useState<Date>(new Date());
  const [answers, setAnswers] = useState<Record<string, AnswerLocal>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingSubmit, setConfirmingSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [autoScore, setAutoScore] = useState<number | null>(null);
  const [autoSubmittedAt, setAutoSubmittedAt] = useState<string | null>(null);

  // Guard against the auto-submit firing more than once if the timer hits
  // zero while a submit is already in flight.
  const autoSubmitFiredRef = useRef(false);

  const init = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const detail = await apiFetch<{
        test: StudentTest;
        submission:
          | (StudentSubmissionEcho & {
              started_at: string | null;
              effective_deadline: string | null;
            })
          | null;
      }>(`/student/tests/${testId}`);
      setTest(detail.test);
      let sub = detail.submission;
      if (!sub) {
        const startRes = await apiFetch<{
          submission: {
            id: string;
            status: "draft";
            started_at: string | null;
            effective_deadline: string | null;
          };
        }>(`/student/tests/${testId}/start`, { method: "POST" });
        sub = {
          id: startRes.submission.id,
          status: startRes.submission.status,
          started_at: startRes.submission.started_at,
          effective_deadline: startRes.submission.effective_deadline,
          answers: [],
        };
      }
      setSubmission(sub);
      setDeadline(
        sub.effective_deadline ? new Date(sub.effective_deadline) : null,
      );
      setAnswers(buildInitialAnswers(detail.test, sub));
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [testId]);

  useEffect(() => {
    void init();
  }, [init]);

  // 1s timer tick. Only runs while a draft is open with a deadline.
  useEffect(() => {
    if (!deadline) return;
    if (submission?.status !== "draft") return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [deadline, submission?.status]);

  function toggleOption(questionId: string, optionId: string, checked: boolean) {
    setAnswers((cur) => {
      const prev = cur[questionId] ?? {
        selected_option_ids: [],
        text_answer: "",
      };
      const set = new Set(prev.selected_option_ids);
      if (checked) set.add(optionId);
      else set.delete(optionId);
      return {
        ...cur,
        [questionId]: { ...prev, selected_option_ids: Array.from(set) },
      };
    });
  }

  function setText(questionId: string, value: string) {
    setAnswers((cur) => ({
      ...cur,
      [questionId]: {
        selected_option_ids: cur[questionId]?.selected_option_ids ?? [],
        text_answer: value,
      },
    }));
  }

  async function saveDraft() {
    if (!submission || !test) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch(`/student/submissions/${submission.id}/answers`, {
        method: "PUT",
        body: JSON.stringify({ answers: serializeAnswers(test, answers) }),
      });
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setSaving(false);
    }
  }

  // Final submit. `flush=true` (manual click) saves once before submitting so
  // unsaved local state lands. `flush=false` (auto-submit on timer expiry)
  // skips the save — the backend would reject it past the deadline anyway.
  const submitNow = useCallback(
    async (opts: { flush: boolean }) => {
      if (!submission || !test) return;
      setSubmitting(true);
      setError(null);
      try {
        if (opts.flush) {
          await apiFetch(`/student/submissions/${submission.id}/answers`, {
            method: "PUT",
            body: JSON.stringify({
              answers: serializeAnswers(test, answers),
            }),
          });
        }
        const res = await apiFetch<SubmitResponse>(
          `/student/submissions/${submission.id}/submit`,
          { method: "POST" },
        );
        setSubmission({
          ...submission,
          status: res.submission.status,
        });
        setSubmittedAt(res.submission.submitted_at);
        setAutoSubmittedAt(res.submission.auto_submitted_at);
        setAutoScore(res.submission.total_auto_score);
        setConfirmingSubmit(false);
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        setSubmitting(false);
      }
    },
    [submission, test, answers],
  );

  // Auto-submit when the timer hits zero. Runs at most once per page load.
  useEffect(() => {
    if (!deadline) return;
    if (submission?.status !== "draft") return;
    if (autoSubmitFiredRef.current) return;
    if (now.getTime() < deadline.getTime()) return;
    autoSubmitFiredRef.current = true;
    void submitNow({ flush: false });
  }, [now, deadline, submission?.status, submitNow]);

  if (loading) return <p>Loading…</p>;
  if (error && !test) return <ErrorBanner error={error} />;
  if (!test || !submission) return <p>Not found.</p>;

  if (submission.status !== "draft") {
    return (
      <SubmissionConfirmation
        title={test.title}
        submittedAt={submittedAt}
        autoSubmittedAt={autoSubmittedAt}
        autoScore={autoScore}
      />
    );
  }

  const remainingMs = deadline
    ? Math.max(0, deadline.getTime() - now.getTime())
    : null;
  const warnLow =
    remainingMs !== null && remainingMs <= FIVE_MINUTES_MS && remainingMs > 0;
  const expired = remainingMs !== null && remainingMs <= 0;

  return (
    <section>
      <h1>{test.title}</h1>
      {test.description && <p>{test.description}</p>}
      {test.duration_minutes !== null && (
        <p>
          <strong>Duration:</strong> {test.duration_minutes} minutes
        </p>
      )}

      {remainingMs !== null && (
        <div
          className={`countdown ${warnLow ? "countdown-warning" : ""}`}
          role="timer"
          aria-live="polite"
        >
          Time remaining: <strong>{formatRemaining(remainingMs)}</strong>
          {warnLow && (
            <span> — less than 5 minutes left, please finish soon.</span>
          )}
          {expired && (
            <span> — time is up. Submitting your current answers…</span>
          )}
        </div>
      )}

      <ErrorBanner error={error} />

      <ol className="questions">
        {test.questions.map((q) => (
          <li key={q.id} className="answer">
            <p>
              <strong>{q.prompt}</strong>{" "}
              <small>({q.points} pts)</small>
            </p>
            <QuestionImages images={q.images} kind="student" />
            {q.type === "mcq" ? (
              <ul className="options">
                {q.answer_options.map((o) => {
                  const checked =
                    answers[q.id]?.selected_option_ids.includes(o.id) ?? false;
                  return (
                    <li key={o.id}>
                      <label>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={expired}
                          onChange={(e) =>
                            toggleOption(q.id, o.id, e.target.checked)
                          }
                        />{" "}
                        {o.text}
                      </label>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <textarea
                aria-label={`answer-${q.id}`}
                rows={8}
                className="student-free-text"
                value={answers[q.id]?.text_answer ?? ""}
                disabled={expired}
                onChange={(e) => setText(q.id, e.target.value)}
              />
            )}
          </li>
        ))}
      </ol>

      <div className="actions">
        <button
          onClick={() => void saveDraft()}
          disabled={saving || submitting || expired}
        >
          {saving ? "Saving…" : "Save draft"}
        </button>{" "}
        <button
          onClick={() => setConfirmingSubmit(true)}
          disabled={saving || submitting || expired}
        >
          Submit final answers
        </button>
      </div>

      {confirmingSubmit && (
        <div role="dialog" aria-label="confirm submit" className="confirm-panel">
          <p>
            <strong>
              After submitting, you cannot change your answers.
            </strong>
          </p>
          <button
            onClick={() => void submitNow({ flush: true })}
            disabled={submitting}
          >
            {submitting ? "Submitting…" : "Confirm submit"}
          </button>{" "}
          <button onClick={() => setConfirmingSubmit(false)} disabled={submitting}>
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}

function SubmissionConfirmation({
  title,
  submittedAt,
  autoSubmittedAt,
  autoScore,
}: {
  title: string;
  submittedAt: string | null;
  autoSubmittedAt: string | null;
  autoScore: number | null;
}) {
  return (
    <section>
      <h1>Submission received</h1>
      <p>
        You have submitted <strong>{title}</strong>. Your answers cannot be
        changed.
      </p>
      {autoSubmittedAt && (
        <p>
          <em>
            Time ran out — your latest saved answers were submitted
            automatically.
          </em>
        </p>
      )}
      {submittedAt && (
        <p>
          <small>Submitted at: {submittedAt}</small>
        </p>
      )}
      {autoScore !== null && (
        <p>
          Auto-graded score so far: <strong>{autoScore}</strong>
        </p>
      )}
      <p>
        <Link to="/student">Back to dashboard</Link>
        {" · "}
        <Link to="/student/results">View my results</Link>
      </p>
    </section>
  );
}

function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

function buildInitialAnswers(
  test: StudentTest,
  submission: StudentSubmissionEcho,
): Record<string, AnswerLocal> {
  const out: Record<string, AnswerLocal> = {};
  for (const q of test.questions) {
    out[q.id] = { selected_option_ids: [], text_answer: "" };
  }
  for (const a of submission.answers) {
    out[a.question_id] = {
      selected_option_ids: a.selected_option_ids,
      text_answer: a.text_answer ?? "",
    };
  }
  return out;
}

function serializeAnswers(
  test: StudentTest,
  answers: Record<string, AnswerLocal>,
): Array<Record<string, unknown>> {
  return test.questions.map((q) => {
    const a = answers[q.id] ?? {
      selected_option_ids: [],
      text_answer: "",
    };
    return q.type === "mcq"
      ? {
          question_id: q.id,
          selected_option_ids: a.selected_option_ids,
        }
      : {
          question_id: q.id,
          text_answer: a.text_answer,
        };
  });
}
